import { sift } from 'radash';
import { useMemo } from 'react';
import {
  CheckoutModel,
  QuarterOverviewModel,
} from 'src/core/model/quarter-overview.model';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import {
  isDelmonicosLine,
  isIgnoredByBreakdownType,
} from '../quarter-overview.utils';
import { CheckoutStats } from './checkout-stats';

type Props = {
  readonly lmsLines?: QuarterOverviewModel[];
  readonly checkoutLines?: CheckoutModel[];
};

export function QuarterOverviewStats(props: Props): React.JSX.Element {
  const { lmsLines = [], checkoutLines = [] } = props;
  const { t } = useTranslation();

  const delmonicosLines = useMemo(
    () => lmsLines.filter(isDelmonicosLine),
    [lmsLines],
  );

  const iTransactionIdList = useMemo(
    () => sift(lmsLines.map((item) => item.iTransactionId)),
    [lmsLines],
  );

  const ignoredCheckoutLines = useMemo(
    () => checkoutLines.filter(isIgnoredByBreakdownType),
    [checkoutLines],
  );

  const associatedCheckoutLines = useMemo(
    () =>
      checkoutLines
        .filter((item) => !isIgnoredByBreakdownType(item))
        .filter((item) => iTransactionIdList.includes(item.iTransactionId)),
    [checkoutLines, iTransactionIdList],
  );

  const unassociatedCheckoutLines = useMemo(
    () =>
      checkoutLines
        .filter((item) => !isIgnoredByBreakdownType(item))
        .filter((item) => !iTransactionIdList.includes(item.iTransactionId)),
    [checkoutLines, iTransactionIdList],
  );

  return (
    <Stack direction="column" gap={1} alignItems="start">
      <Typography>
        {t('quarter-overview.stats.lms-lines', {
          count: lmsLines.length,
        })}
      </Typography>
      <Typography>
        {t('quarter-overview.stats.delmonicos-lines', {
          count: delmonicosLines.length,
        })}
      </Typography>

      <CheckoutStats
        list={checkoutLines}
        title={t('quarter-overview.stats.checkout-lines', {
          count: checkoutLines.length,
        })}
      />

      <CheckoutStats
        list={ignoredCheckoutLines}
        title={t('quarter-overview.stats.ignored-checkout-lines', {
          count: ignoredCheckoutLines.length,
        })}
      />

      <CheckoutStats
        list={associatedCheckoutLines}
        title={t('quarter-overview.stats.associated-checkout-lines', {
          count: associatedCheckoutLines.length,
        })}
      />

      <CheckoutStats
        list={unassociatedCheckoutLines}
        title={t('quarter-overview.stats.unassociated-checkout-lines', {
          count: unassociatedCheckoutLines.length,
        })}
      />
    </Stack>
  );
}
